/**
 * Professions (อาชีพ, see docs/systems/PROFESSIONS.md): gathering trades that take goods from the land
 * (wood, ore, fish, herbs, crops) and crafting trades that make them into gear, food and medicine.
 * Everyone may learn every trade; each has its own level 1–10. Goods are stacks kept apart from the
 * equipment bag; gear made here is ordinary equipment (data/items, the `smith-` and `tailor-` ids).
 * Rules are in `professions.ts`; this file is only the data.
 */
import { CRAFT_GOODS } from './itemCraft';
import { BREAKTHROUGH_GOODS, BREAKTHROUGH_RECIPES } from './breakthrough';
import { TOOL_RECIPES } from './tools';
import { QG_GOODS } from './qinggongDrops';
import { SECT_PAGE_GOODS } from './sectActivities';
import { START, type ZoneId } from './zones';

export type ProfessionId =
  'woodcutter' | 'miner' | 'fisher' | 'herbalist' | 'farmer' | 'blacksmith' | 'tailor' | 'cook';

/**
 * Work poses the rig already draws for the townsfolk (render/sprites.ts `rigFor`), each with its
 * tool (render/rig/tools.ts). Chopping, mining, smithing and cooking borrow the closest of them.
 */
export type WorkPose = 'hoe' | 'fish' | 'sweep' | 'mend' | 'chop' | 'pick' | 'hammer' | 'water' | 'sow';

/**
 * The poses the figures draw today (render/emotes.ts `work-*`). A job in another pose is shown in the closest drawn one until
 * the pose lane (docs/team/inbox/character-anim.md) draws it: add the pose here when it does.
 */
export const DRAWN_POSES: ReadonlySet<WorkPose> = new Set<WorkPose>(['hoe', 'fish', 'sweep', 'mend']);
const POSE_FALLBACK: Readonly<Partial<Record<WorkPose, WorkPose>>> = {
  chop: 'hoe',
  pick: 'hoe',
  hammer: 'hoe',
  water: 'sweep',
  sow: 'hoe',
};
/** The pose actually shown for a job's pose. */
export const shownPose = (p: WorkPose): WorkPose => (DRAWN_POSES.has(p) ? p : (POSE_FALLBACK[p] ?? 'hoe'));

export interface ProfessionDef {
  readonly id: ProfessionId;
  readonly name: string;
  /** gather: takes goods from nodes; craft: makes them into things; both: herbalists pick and brew. */
  readonly kind: 'gather' | 'craft' | 'both';
  readonly desc: string;
  /** The pose crafting in this trade is done in. */
  readonly pose: WorkPose;
}

const professions: ProfessionDef[] = [
  {
    id: 'woodcutter',
    name: 'คนตัดไม้',
    kind: 'gather',
    pose: 'chop',
    desc: 'ตัดไม้จากต้นสนและต้นโอ๊กเก่า ส่งให้ช่างตีเหล็กทำด้ามและถ่าน',
  },
  {
    id: 'miner',
    name: 'คนขุดแร่',
    kind: 'gather',
    pose: 'pick',
    desc: 'ขุดแร่จากสายแร่ในหินผา วัตถุดิบหลักของช่างตีเหล็ก',
  },
  {
    id: 'fisher',
    name: 'ชาวประมง',
    kind: 'gather',
    pose: 'fish',
    desc: 'ตกปลาริมน้ำและปลายท่า ปลาดีส่งพ่อครัว',
  },
  {
    id: 'herbalist',
    name: 'นักสมุนไพร',
    kind: 'both',
    pose: 'mend',
    desc: 'ขุดสมุนไพรใต้ร่มไม้ แล้วบดปรุงเป็นยาฟื้นพลังชีวิตและลมปราณ',
  },
  {
    id: 'farmer',
    name: 'ชาวนา',
    kind: 'gather',
    pose: 'hoe',
    desc: 'ปลูกข้าว ผัก ฝ้าย ชา หม่อน ถั่วเหลือง และฟักทองในแปลงนา รดน้ำ ใส่ปุ๋ย รอโตแล้วเกี่ยว',
  },
  {
    id: 'blacksmith',
    name: 'ช่างตีเหล็ก',
    kind: 'craft',
    pose: 'hammer',
    desc: 'หลอมแร่เป็นเหล็กแท่ง ตีเป็นกระบี่ หมวก และเสื้อเกราะ',
  },
  {
    id: 'tailor',
    name: 'ช่างตัดเสื้อ',
    kind: 'craft',
    pose: 'mend',
    desc: 'ทอผ้า ย้อมคราม ตัดเย็บเสื้อคลุม งอบ และผ้าโพก',
  },
  {
    id: 'cook',
    name: 'พ่อครัว',
    kind: 'craft',
    pose: 'sweep',
    desc: 'ปรุงอาหารที่ฟื้นพลังชีวิตและให้พลังก่อนออกล่า',
  },
];
export const PROFESSION_LIST: readonly ProfessionDef[] = professions;
export const PROFESSIONS: Readonly<Record<ProfessionId, ProfessionDef>> = Object.fromEntries(
  professions.map((p) => [p.id, p]),
) as Record<ProfessionId, ProfessionDef>;
export const isProfessionId = (v: unknown): v is ProfessionId => typeof v === 'string' && v in PROFESSIONS;

/** Highest level of a trade. */
export const TRADE_MAX_LEVEL = 10;
/** Trade xp to go from `level` to the next. */
export const tradeXpToNext = (level: number): number => Math.round(30 * level ** 1.6);

/** Most of one good carried. */
export const GOOD_STACK = 999;

/** What a good does when used: heals (fractions of the maximum) or a status (food buffs). */
export interface GoodUse {
  readonly hp?: number;
  readonly qi?: number;
  readonly status?: string;
  /** Fills the training-boost ledger's `item` slot (trainBoost.ts): +bonus for `seconds` (one slot, never stacks). */
  readonly train?: { readonly bonus: number; readonly seconds: number };
}

/** A stackable good: material, seed, food or medicine. */
export interface GoodDef {
  readonly id: string;
  readonly name: string;
  readonly kind: 'material' | 'seed' | 'food' | 'medicine';
  readonly desc: string;
  /** What the merchant pays for one (ตำลึง). */
  readonly price: number;
  /** What the merchant sells one for, when he sells it at all. */
  readonly buy?: number;
  readonly use?: GoodUse;
}

const goods: GoodDef[] = [
  // ── gathered ──
  {
    id: 'pine-log',
    name: 'ไม้สน',
    kind: 'material',
    price: 3,
    desc: 'ท่อนไม้สนเนื้ออ่อน ใช้เผาถ่านและทำด้าม',
  },
  {
    id: 'hard-wood',
    name: 'ไม้เนื้อแข็ง',
    kind: 'material',
    price: 9,
    desc: 'ไม้โอ๊กเก่าเนื้อแน่น ทำด้ามอาวุธชั้นดี',
  },
  { id: 'iron-ore', name: 'แร่เหล็ก', kind: 'material', price: 4, desc: 'ก้อนแร่สีสนิม หลอมได้เหล็กแท่ง' },
  {
    id: 'black-iron',
    name: 'แร่เหล็กดำ',
    kind: 'material',
    price: 14,
    desc: 'แร่หนักสีดำจากยอดเขาหิมะ เหนียวกว่าเหล็กธรรมดา',
  },
  { id: 'carp', name: 'ปลาไน', kind: 'material', price: 4, desc: 'ปลาน้ำจืดตัวอ้วน ย่างหรือต้มก็อร่อย' },
  {
    id: 'mandarin-fish',
    name: 'ปลากุ้ยฮวา',
    kind: 'material',
    price: 18,
    desc: 'ปลาเนื้อหวานหายาก ของโปรดของคหบดี',
  },
  {
    id: 'herb-leaf',
    name: 'ใบสมุนไพร',
    kind: 'material',
    price: 3,
    desc: 'ใบยาสามัญ บดเป็นยาหรือต้มเป็นสีคราม',
  },
  {
    id: 'lingzhi',
    name: 'เห็ดหลินจือ',
    kind: 'material',
    price: 30,
    desc: 'เห็ดอายุยืนใต้ไม้ใหญ่ บำรุงทั้งกายและลมปราณ',
  },
  {
    id: 'wolf-meat',
    name: 'เนื้อหมาป่า',
    kind: 'material',
    price: 4,
    desc: 'เนื้อหมาป่าเหนียวกลิ่นแรง ร้านเหล้ารับซื้อไปตุ๋นยา',
  },
  { id: 'rice', name: 'ข้าวเปลือก', kind: 'material', price: 3, desc: 'ข้าวจากนาของตัวเอง' },
  { id: 'straw', name: 'ฟางข้าว', kind: 'material', price: 1, desc: 'ฟางแห้ง สานเป็นงอบได้' },
  { id: 'cabbage', name: 'ผักกาด', kind: 'material', price: 3, desc: 'ผักกาดหัวใหญ่ ต้มแกงจืดหวานชื่นใจ' },
  { id: 'cotton', name: 'ฝ้าย', kind: 'material', price: 5, desc: 'ปุยฝ้ายขาว ปั่นทอเป็นผ้า' },
  // ── bought from the merchant ──
  {
    id: 'rice-seed',
    name: 'เมล็ดข้าว',
    kind: 'seed',
    price: 1,
    buy: 4,
    desc: 'ปลูกในแปลงนา เกี่ยวได้ข้าวและฟาง',
  },
  { id: 'cabbage-seed', name: 'เมล็ดผักกาด', kind: 'seed', price: 1, buy: 5, desc: 'ปลูกในแปลงนา โตเร็ว' },
  {
    id: 'cotton-seed',
    name: 'เมล็ดฝ้าย',
    kind: 'seed',
    price: 2,
    buy: 8,
    desc: 'ปลูกในแปลงนา โตช้าแต่ขายดี',
  },
  { id: 'tea-seed', name: 'เมล็ดชา', kind: 'seed', price: 3, buy: 12, desc: 'ปลูกในแปลงนา โตช้า ได้ใบชา' },
  { id: 'mulberry-seed', name: 'เมล็ดหม่อน', kind: 'seed', price: 3, buy: 12, desc: 'ปลูกในแปลงนา ได้ใบหม่อนเลี้ยงไหม' },
  { id: 'soybean-seed', name: 'เมล็ดถั่วเหลือง', kind: 'seed', price: 2, buy: 7, desc: 'ปลูกในแปลงนา โตเร็วปานกลาง' },
  { id: 'pumpkin-seed', name: 'เมล็ดฟักทอง', kind: 'seed', price: 2, buy: 9, desc: 'ปลูกในแปลงนา ได้ฟักทองลูกโต' },
  { id: 'tea-leaf', name: 'ใบชา', kind: 'material', price: 8, desc: 'ใบชาเขียวสด เก็บจากไร่ของตัวเอง' },
  { id: 'mulberry-leaf', name: 'ใบหม่อน', kind: 'material', price: 6, desc: 'ใบหม่อนอ่อน อาหารของหนอนไหม' },
  { id: 'soybean', name: 'ถั่วเหลือง', kind: 'material', price: 4, desc: 'ฝักถั่วเหลืองแก่จัด' },
  { id: 'pumpkin', name: 'ฟักทอง', kind: 'material', price: 6, desc: 'ฟักทองลูกกลมสีทอง' },
  { id: 'manure', name: 'ปุ๋ยคอก', kind: 'material', price: 2, buy: 6, desc: 'โรยลงแปลงนา พืชโตเร็วขึ้นและได้ผลผลิตมากขึ้น' },
  { id: 'bait', name: 'เหยื่อตกปลา', kind: 'material', price: 1, buy: 3, desc: 'ใช้หนึ่งชิ้นต่อการตกหนึ่งครั้ง ปลาหายากติดง่ายขึ้นเท่าตัว' },
  {
    id: 'silk-thread',
    name: 'ด้ายไหม',
    kind: 'material',
    price: 6,
    buy: 15,
    desc: 'ด้ายไหมจากทางใต้ พ่อค้าเร่นำมาขาย',
  },
  // ── made ──
  { id: 'charcoal', name: 'ถ่านไม้', kind: 'material', price: 5, desc: 'ถ่านไฟแรง ใช้หลอมเหล็ก' },
  { id: 'iron-ingot', name: 'เหล็กแท่ง', kind: 'material', price: 14, desc: 'เหล็กหลอมพร้อมตี' },
  { id: 'black-ingot', name: 'เหล็กดำแท่ง', kind: 'material', price: 40, desc: 'เหล็กดำหลอมแล้ว คมไม่บิ่น' },
  { id: 'cotton-cloth', name: 'ผ้าฝ้าย', kind: 'material', price: 15, desc: 'ผ้าฝ้ายทอมือหนึ่งพับ' },
  { id: 'indigo-dye', name: 'สีคราม', kind: 'material', price: 10, desc: 'น้ำย้อมครามจากใบสมุนไพรหมัก' },
  { id: 'silk-cloth', name: 'ผ้าไหม', kind: 'material', price: 35, desc: 'ผ้าไหมเนื้อมันวาว' },
  // The rice → mantou chain (docs/handoff/newdir/ITEMS.md): rice milled at a mill, the flour steamed.
  { id: 'flour', name: 'แป้งข้าว', kind: 'material', price: 5, desc: 'ข้าวโม่ละเอียดที่โรงโม่ นึ่งเป็นหมั่นโถวได้' },
  {
    id: 'mantou',
    name: 'หมั่นโถว',
    kind: 'food',
    price: 12,
    desc: 'หมั่นโถวนึ่งร้อน ๆ ฟื้นพลังชีวิต 20% และอิ่มท้อง วิ่งเร็วขึ้น 5% รับความเสียหายลดลง 5% นาน 3 นาที',
    use: { hp: 0.2, status: 'meal-mantou' },
  },
  {
    id: 'rice-porridge',
    name: 'โจ๊กข้าว',
    kind: 'food',
    price: 8,
    desc: 'ฟื้นพลังชีวิต 25% ทันที',
    use: { hp: 0.25 },
  },
  {
    id: 'grilled-carp',
    name: 'ปลาไนย่าง',
    kind: 'food',
    price: 14,
    desc: 'อิ่มท้อง ตีแรงขึ้น 8% นาน 10 นาที',
    use: { status: 'meal-might' },
  },
  {
    id: 'cabbage-soup',
    name: 'แกงจืดผักกาด',
    kind: 'food',
    price: 14,
    desc: 'อุ่นกาย รับความเสียหายลดลง 8% นาน 10 นาที',
    use: { status: 'meal-guard' },
  },
  {
    id: 'mandarin-feast',
    name: 'ปลากุ้ยฮวานึ่ง',
    kind: 'food',
    price: 60,
    desc: 'งานเลี้ยงย่อม ตีแรงขึ้น 10% รับความเสียหายลดลง 6% คริติคอล +3% นาน 10 นาที',
    use: { status: 'meal-feast' },
  },
  {
    id: 'heal-pill',
    name: 'ยาสมานแผล',
    kind: 'medicine',
    price: 10,
    desc: 'ฟื้นพลังชีวิต 35% ทันที',
    use: { hp: 0.35 },
  },
  {
    id: 'qi-pill',
    name: 'ยาบำรุงลมปราณ',
    kind: 'medicine',
    price: 12,
    desc: 'ฟื้นลมปราณ 40% ทันที',
    use: { qi: 0.4 },
  },
  {
    id: 'lingzhi-elixir',
    name: 'ยาเห็ดหลินจือ',
    kind: 'medicine',
    price: 70,
    desc: 'ฟื้นพลังชีวิต 70% และลมปราณ 40% ทันที',
    use: { hp: 0.7, qi: 0.4 },
  },
  {
    // Sold only by the black-market buyers (data/shop.ts `BLACK_MARKET`); blown with `blowPowder` (systems/offlineWork.ts).
    id: 'knockout-powder',
    name: 'ยาสลบ',
    kind: 'medicine',
    price: 15,
    desc: 'ผงยาสลบในกระบอกไม้ไผ่ เป่าใส่ร่างออฟไลน์ในระยะ 2 เมตร ให้หลับแล้วจับใส่กระสอบ',
  },  // Items v2 smith materials and gems (data/itemCraft.ts).
  // ของช่วยฝึก (I4): resin is cooked from pine logs; tea and incense fill the training-boost item slot.
  { id: 'resin', name: 'ยางสน', kind: 'material', price: 4, desc: 'ยางเหนียวกลั่นจากไม้สน ใช้ทำธูป' },
  {
    id: 'mind-tea',
    name: 'ชาบำรุงจิต',
    kind: 'food',
    price: 12,
    desc: 'ชาใบไม้ต้มหอม ดื่มแล้วฝึกปรือเร็วขึ้น 30% นาน 20 นาที (ใช้ซ้ำกับธูปไม่ซ้อนกัน)',
    use: { train: { bonus: 0.3, seconds: 1200 } },
  },
  {
    id: 'calm-incense',
    name: 'ธูปสงบจิต',
    kind: 'medicine',
    price: 20,
    desc: 'ธูปยางสนกลิ่นสงบ จุดแล้วฝึกปรือเร็วขึ้น 50% นาน 30 นาที (ใช้ซ้ำกับชาไม่ซ้อนกัน)',
    use: { train: { bonus: 0.5, seconds: 1800 } },
  },
  ...CRAFT_GOODS,
  ...BREAKTHROUGH_GOODS,
  // ตำราวิชาตัวเบา: pages, manuals and the ตราเมฆา (data/qinggongDrops.ts).
  ...QG_GOODS,
  ...SECT_PAGE_GOODS,
];
export const GOOD_LIST: readonly GoodDef[] = goods;
export const GOODS: Readonly<Record<string, GoodDef>> = Object.fromEntries(goods.map((g) => [g.id, g]));

/** Seconds between two uses of a medicine or a meal. */
export const GOOD_COOLDOWN = 8;

/** A good and how many: a recipe's input, or a yield. */
export interface GoodCount {
  readonly id: string;
  readonly n: number;
}

/** One possible yield of a gather: from `lv` up, `chance` of [min, max] of a good. */
export interface Yield {
  readonly id: string;
  readonly min: number;
  readonly max: number;
  readonly chance: number;
  /** Trade level this yield needs (the node's own level if left out). */
  readonly lv?: number;
}

/** What may stand at a node: the map tile beside it the node needs (see `placeNodes`); `open` any open ground. */
export type NodeTerrain = 'tree' | 'rock' | 'water' | 'herb' | 'field' | 'open';

/** A kind of resource node: a pine, an iron vein, a fishing spot, a herb patch, a field plot. */
export interface NodeKindDef {
  readonly id: string;
  readonly name: string;
  readonly prof: ProfessionId;
  readonly level: number;
  readonly terrain: NodeTerrain;
  /** The verb on the prompt (ตัดไม้, ขุดแร่, ตกปลา…). */
  readonly verb: string;
  readonly pose: WorkPose;
  /** Seconds a gather takes at the node's own level. */
  readonly time: number;
  readonly xp: number;
  readonly yields: readonly Yield[];
  /** Gathers before the node is spent, and the seconds it takes to come back. Plots: unused. */
  readonly uses: number;
  readonly regrow: number;
  /** A work station (a mill): not gathered; recipes with this `station` are made only beside it. */
  readonly station?: boolean;
}

const kinds: NodeKindDef[] = [
  {
    id: 'pine',
    name: 'ต้นสน',
    prof: 'woodcutter',
    level: 1,
    terrain: 'tree',
    verb: 'ตัดไม้',
    pose: 'chop',
    time: 4,
    xp: 8,
    uses: 3,
    regrow: 90,
    yields: [{ id: 'pine-log', min: 1, max: 2, chance: 1 }],
  },
  {
    id: 'oak',
    name: 'ต้นโอ๊กเก่า',
    prof: 'woodcutter',
    level: 4,
    terrain: 'tree',
    verb: 'ตัดไม้',
    pose: 'chop',
    time: 5,
    xp: 16,
    uses: 3,
    regrow: 150,
    yields: [
      { id: 'hard-wood', min: 1, max: 2, chance: 1 },
      { id: 'pine-log', min: 1, max: 1, chance: 0.5 },
    ],
  },
  {
    id: 'iron-vein',
    name: 'สายแร่เหล็ก',
    prof: 'miner',
    level: 1,
    terrain: 'rock',
    verb: 'ขุดแร่',
    pose: 'pick',
    time: 4.5,
    xp: 9,
    uses: 3,
    regrow: 120,
    yields: [{ id: 'iron-ore', min: 1, max: 2, chance: 1 }],
  },
  {
    id: 'dark-vein',
    name: 'สายแร่เหล็กดำ',
    prof: 'miner',
    level: 5,
    terrain: 'rock',
    verb: 'ขุดแร่',
    pose: 'pick',
    time: 6,
    xp: 20,
    uses: 3,
    regrow: 180,
    yields: [
      { id: 'black-iron', min: 1, max: 2, chance: 1 },
      { id: 'iron-ore', min: 1, max: 1, chance: 0.5 },
    ],
  },
  {
    id: 'fish-spot',
    name: 'จุดตกปลา',
    prof: 'fisher',
    level: 1,
    terrain: 'water',
    verb: 'ตกปลา',
    pose: 'fish',
    time: 5,
    xp: 9,
    uses: 4,
    regrow: 90,
    yields: [
      { id: 'carp', min: 1, max: 1, chance: 1 },
      { id: 'mandarin-fish', min: 1, max: 1, chance: 0.25, lv: 4 },
    ],
  },
  {
    id: 'herb-patch',
    name: 'กอสมุนไพร',
    prof: 'herbalist',
    level: 1,
    terrain: 'herb',
    verb: 'เก็บสมุนไพร',
    pose: 'hoe',
    time: 3.5,
    xp: 8,
    uses: 3,
    regrow: 100,
    yields: [
      { id: 'herb-leaf', min: 1, max: 3, chance: 1 },
      { id: 'lingzhi', min: 1, max: 1, chance: 0.15, lv: 5 },
    ],
  },
  {
    // A plot is planted (`plant`), grows, then is harvested (`gather`): see CROPS.
    id: 'plot',
    name: 'แปลงนา',
    prof: 'farmer',
    level: 1,
    terrain: 'field',
    verb: 'เกี่ยว',
    pose: 'sweep',
    time: 4,
    xp: 0,
    uses: 1,
    regrow: 0,
    yields: [],
  },
  {
    // A station: rice is milled into flour beside it (recipe `flour`), from the trades window.
    id: 'mill',
    name: 'โรงโม่แป้ง',
    prof: 'cook',
    level: 1,
    terrain: 'open',
    verb: 'โม่แป้ง',
    pose: 'sweep',
    time: 0,
    xp: 0,
    uses: 1,
    regrow: 0,
    yields: [],
    station: true,
  },
];
export const NODE_KINDS: Readonly<Record<string, NodeKindDef>> = Object.fromEntries(
  kinds.map((k) => [k.id, k]),
);

/** Seconds planting a seed takes. */
export const PLANT_TIME = 3;

/** A crop: the seed it grows from, how long, and what the harvest gives. */
export interface CropDef {
  readonly seed: string;
  readonly name: string;
  readonly level: number;
  /** Seconds of real time from planting to ripe (rice 3 h = 10800; game time is real time, offline included). */
  readonly grow: number;
  readonly xp: number;
  readonly yields: readonly Yield[];
}

const crops: CropDef[] = [
  {
    seed: 'rice-seed',
    name: 'ข้าว',
    level: 1,
    grow: 10800,
    xp: 14,
    yields: [
      { id: 'rice', min: 3, max: 5, chance: 1 },
      { id: 'straw', min: 2, max: 3, chance: 1 },
    ],
  },
  {
    seed: 'cabbage-seed',
    name: 'ผักกาด',
    level: 2,
    grow: 8100,
    xp: 14,
    yields: [{ id: 'cabbage', min: 2, max: 4, chance: 1 }],
  },
  {
    seed: 'cotton-seed',
    name: 'ฝ้าย',
    level: 3,
    grow: 16200,
    xp: 24,
    yields: [{ id: 'cotton', min: 3, max: 4, chance: 1 }],
  },
  {
    seed: 'soybean-seed',
    name: 'ถั่วเหลือง',
    level: 3,
    grow: 9900,
    xp: 18,
    yields: [{ id: 'soybean', min: 3, max: 5, chance: 1 }],
  },
  {
    seed: 'pumpkin-seed',
    name: 'ฟักทอง',
    level: 4,
    grow: 13500,
    xp: 24,
    yields: [{ id: 'pumpkin', min: 2, max: 3, chance: 1 }],
  },
  {
    seed: 'tea-seed',
    name: 'ชา',
    level: 5,
    grow: 18900,
    xp: 34,
    yields: [{ id: 'tea-leaf', min: 3, max: 4, chance: 1 }],
  },
  {
    seed: 'mulberry-seed',
    name: 'หม่อน',
    level: 6,
    grow: 18000,
    xp: 34,
    yields: [{ id: 'mulberry-leaf', min: 3, max: 5, chance: 1 }],
  },
];
/** Crops by their seed. */
export const CROPS: Readonly<Record<string, CropDef>> = Object.fromEntries(crops.map((c) => [c.seed, c]));

/**
 * Where nodes stand, per zone: near a named spot of the map ('plaza' or one of its landmarks), the
 * offset in tiles, then moved to the nearest tile that fits the node (a tree beside it for a pine,
 * open water for a fishing spot...). The id is stable: a planted plot is saved under it.
 */
export interface NodeSpot {
  readonly id: string;
  readonly zone: ZoneId;
  readonly kind: string;
  readonly at: string;
  readonly dx: number;
  readonly dy: number;
}

const spot = (zone: ZoneId, kind: string, n: number, at: string, dx: number, dy: number): NodeSpot => ({
  id: `${zone}-${kind}-${n}`,
  zone,
  kind,
  at,
  dx,
  dy,
});

export const NODE_SPOTS: readonly NodeSpot[] = [
  // หมู่บ้านหยานหยู: the starting village (docs/maps/YANYU.md). Pines, an oak and herbs in the
  // wood north-east of the village, ore at the quarry under the western cliffs and in the eastern
  // range, the pier and the shore for fishing, the rice fields south of the gate for farming.
  spot(START, 'pine', 1, 'grove', -3, 3),
  spot(START, 'pine', 2, 'grove', 3, -3),
  spot(START, 'pine', 3, 'grove', -6, -5),
  spot(START, 'oak', 1, 'grove', 5, 5),
  spot(START, 'iron-vein', 1, 'quarry', 0, 0),
  spot(START, 'iron-vein', 2, 'quarry', 0, 12),
  spot(START, 'fish-spot', 1, 'pierWest', 0, 0),
  spot(START, 'fish-spot', 2, 'pierEast', 0, 0),
  spot(START, 'fish-spot', 3, 'shore', 0, 0),
  spot(START, 'herb-patch', 1, 'grove', -8, 7),
  spot(START, 'herb-patch', 2, 'grove', -8, 7),
  // ลานศิลาเก้ากระบี่: groves and boulders round the town.
  spot('plains', 'pine', 1, 'plaza', -16, -14),
  spot('plains', 'pine', 2, 'plaza', 16, 14),
  spot('plains', 'oak', 1, 'plaza', -20, 18),
  spot('plains', 'oak', 2, 'plaza', 22, -16),
  spot('plains', 'iron-vein', 1, 'plaza', 18, 4),
  spot('plains', 'iron-vein', 2, 'plaza', -18, -4),
  spot('plains', 'herb-patch', 1, 'plaza', -14, 20),
  spot('plains', 'herb-patch', 2, 'plaza', 14, -20),
  // The 256 m plains' pond and the terraced farm have their own (BIG_PLAINS_NODES); the 72 m map has none of either.
  spot('plains', 'fish-spot', 1, 'plaza', 0, 0),
  spot('plains', 'fish-spot', 2, 'plaza', 0, 0),
  spot('plains', 'plot', 1, 'plaza', 0, 0),
  spot('plains', 'plot', 2, 'plaza', 0, 0),
  spot('plains', 'plot', 3, 'plaza', 0, 0),
  spot('plains', 'plot', 4, 'plaza', 0, 0),
  spot('plains', 'plot', 5, 'plaza', 0, 0),
  spot('plains', 'plot', 6, 'plaza', 0, 0),
  // ยอดเขาหิมะ: black iron in the high rock.
  spot('snow', 'dark-vein', 1, 'plaza', 14, -10),
  spot('snow', 'dark-vein', 2, 'plaza', -14, 10),
  spot('snow', 'pine', 1, 'plaza', -12, -12),
  spot('snow', 'herb-patch', 1, 'plaza', 12, 12),
  // หยานหยู (ใหม่): rice plots on the paddy block of ทุ่งนาริมน้ำ (data/layouts/yanyu2.ts: x 96–116, y 282–300) west
  // of the plaza (300, 300), and the mill on the open ground just east of it.
  spot('yanyu2', 'plot', 1, 'plaza', -200, -10),
  spot('yanyu2', 'plot', 2, 'plaza', -196, -6),
  spot('yanyu2', 'plot', 3, 'plaza', -192, -10),
  spot('yanyu2', 'plot', 4, 'plaza', -196, -14),
  spot('yanyu2', 'plot', 5, 'plaza', -188, -6),
  spot('yanyu2', 'plot', 6, 'plaza', -200, -2),
  spot('yanyu2', 'mill', 1, 'plaza', -180, -6),
];

/**
 * The plains' nodes on the 256 m map (PLAINS256): the same nodes, out past the town where its woods, rocks and herbs
 * are (offsets from the plaza, metres), clear of the monster pockets. The 72 m map keeps the offsets above.
 */
export const BIG_PLAINS_NODES: Readonly<Record<string, readonly [number, number]>> = {
  'plains-pine-1': [-56, -49],
  'plains-pine-2': [40, 35],
  'plains-oak-1': [-50, 45],
  'plains-oak-2': [55, -40],
  'plains-iron-vein-1': [24, 59],
  'plains-iron-vein-2': [-45, -10],
  'plains-herb-patch-1': [-35, 50],
  'plains-herb-patch-2': [35, -50],
  // The lily pond and the stream (data/layouts/plains.ts fishing spots), and six plots on the terraced farm (rect 128,202,60,30).
  'plains-fish-spot-1': [36, 27],
  'plains-fish-spot-2': [20, 22],
  'plains-plot-1': [24, 84],
  'plains-plot-2': [28, 84],
  'plains-plot-3': [32, 84],
  'plains-plot-4': [24, 88],
  'plains-plot-5': [28, 88],
  'plains-plot-6': [32, 88],
};

/** Nodes only the 256 m plains have (their offsets are in BIG_PLAINS_NODES; the 72 m map has no pond or farm). */
export const BIG_PLAINS_ONLY: ReadonlySet<string> = new Set([
  'plains-fish-spot-1',
  'plains-fish-spot-2',
  ...[1, 2, 3, 4, 5, 6].map((n) => `plains-plot-${n}`),
]);

export const NODE_SPOT_IDS: ReadonlySet<string> = new Set(NODE_SPOTS.map((s) => s.id));

/** What a recipe makes: a stack of goods, or a piece of equipment into the bag. */
export interface RecipeDef {
  readonly id: string;
  readonly prof: ProfessionId;
  readonly level: number;
  readonly inputs: readonly GoodCount[];
  /** A good (`good`), an equipment item (`item`, data/items) or a tool for the belt (`tool`, data/tools.ts). */
  readonly out: { readonly good: string; readonly n: number } | { readonly item: string } | { readonly tool: string };
  /** Seconds one takes. */
  readonly time: number;
  readonly xp: number;
  /** Made only beside a station node of this kind (`NodeKindDef.station`): the mill for flour. */
  readonly station?: string;
}

const r = (
  id: string,
  prof: ProfessionId,
  level: number,
  inputs: [string, number][],
  out: RecipeDef['out'],
  time: number,
  xp: number,
): RecipeDef => ({ id, prof, level, inputs: inputs.map(([i, n]) => ({ id: i, n })), out, time, xp });

const recipes: RecipeDef[] = [
  // ── ช่างตีเหล็ก ──
  r('charcoal', 'blacksmith', 1, [['pine-log', 2]], { good: 'charcoal', n: 1 }, 2, 4),
  r(
    'iron-ingot',
    'blacksmith',
    1,
    [
      ['iron-ore', 2],
      ['charcoal', 1],
    ],
    { good: 'iron-ingot', n: 1 },
    3,
    8,
  ),
  r(
    'black-ingot',
    'blacksmith',
    5,
    [
      ['black-iron', 2],
      ['charcoal', 2],
    ],
    { good: 'black-ingot', n: 1 },
    4,
    18,
  ),
  r(
    'smith-sword',
    'blacksmith',
    2,
    [
      ['iron-ingot', 3],
      ['pine-log', 1],
    ],
    { item: 'smith-sword' },
    6,
    30,
  ),
  r(
    'smith-helm',
    'blacksmith',
    3,
    [
      ['iron-ingot', 2],
      ['cotton-cloth', 1],
    ],
    { item: 'smith-helm' },
    6,
    34,
  ),
  r(
    'smith-mail',
    'blacksmith',
    5,
    [
      ['iron-ingot', 4],
      ['cotton-cloth', 2],
    ],
    { item: 'smith-mail' },
    8,
    60,
  ),
  r(
    'smith-blade',
    'blacksmith',
    7,
    [
      ['black-ingot', 3],
      ['hard-wood', 1],
    ],
    { item: 'smith-blade' },
    10,
    110,
  ),
  // ── ช่างตัดเสื้อ ──
  r('cotton-cloth', 'tailor', 1, [['cotton', 3]], { good: 'cotton-cloth', n: 1 }, 3, 8),
  r('indigo-dye', 'tailor', 1, [['herb-leaf', 3]], { good: 'indigo-dye', n: 1 }, 2, 6),
  r(
    'silk-cloth',
    'tailor',
    4,
    [
      ['silk-thread', 2],
      ['cotton-cloth', 1],
    ],
    { good: 'silk-cloth', n: 1 },
    4,
    18,
  ),
  r('tailor-hat', 'tailor', 2, [['straw', 6]], { item: 'tailor-hat' }, 5, 24),
  r(
    'tailor-robe',
    'tailor',
    2,
    [
      ['cotton-cloth', 2],
      ['indigo-dye', 1],
    ],
    { item: 'tailor-robe' },
    6,
    30,
  ),
  r(
    'tailor-silk',
    'tailor',
    5,
    [
      ['silk-cloth', 3],
      ['indigo-dye', 1],
    ],
    { item: 'tailor-silk' },
    8,
    60,
  ),
  r(
    'tailor-turban',
    'tailor',
    7,
    [
      ['silk-cloth', 2],
      ['lingzhi', 1],
    ],
    { item: 'tailor-turban' },
    8,
    90,
  ),
  // ── พ่อครัว ──
  r('rice-porridge', 'cook', 1, [['rice', 2]], { good: 'rice-porridge', n: 1 }, 3, 6),
  { ...r('flour', 'cook', 1, [['rice', 3]], { good: 'flour', n: 2 }, 3, 6), station: 'mill' },
  r('mantou', 'cook', 1, [['flour', 2]], { good: 'mantou', n: 2 }, 4, 8),
  r(
    'grilled-carp',
    'cook',
    1,
    [
      ['carp', 1],
      ['pine-log', 1],
    ],
    { good: 'grilled-carp', n: 1 },
    3,
    8,
  ),
  r(
    'cabbage-soup',
    'cook',
    2,
    [
      ['cabbage', 2],
      ['carp', 1],
    ],
    { good: 'cabbage-soup', n: 1 },
    4,
    12,
  ),
  r(
    'mandarin-feast',
    'cook',
    5,
    [
      ['mandarin-fish', 1],
      ['rice', 2],
      ['herb-leaf', 2],
    ],
    { good: 'mandarin-feast', n: 1 },
    6,
    30,
  ),
  // ── นักสมุนไพร ──
  r('heal-pill', 'herbalist', 1, [['herb-leaf', 3]], { good: 'heal-pill', n: 1 }, 3, 8),
  r(
    'qi-pill',
    'herbalist',
    2,
    [
      ['herb-leaf', 2],
      ['rice', 1],
    ],
    { good: 'qi-pill', n: 1 },
    3,
    10,
  ),
  r(
    'lingzhi-elixir',
    'herbalist',
    5,
    [
      ['lingzhi', 1],
      ['herb-leaf', 2],
    ],
    { good: 'lingzhi-elixir', n: 1 },
    5,
    30,
  ),
  // ── เกษตรและเหยื่อ (lane ITEMS) ──
  r('manure', 'farmer', 2, [['straw', 4]], { good: 'manure', n: 2 }, 3, 6),
  r('bait', 'cook', 1, [['flour', 1]], { good: 'bait', n: 5 }, 2, 3),
  // ── ของช่วยฝึก (I4) ──
  r('resin', 'herbalist', 1, [['pine-log', 3]], { good: 'resin', n: 1 }, 4, 6),
  r('mind-tea', 'herbalist', 2, [['herb-leaf', 3]], { good: 'mind-tea', n: 1 }, 4, 10),
  r(
    'calm-incense',
    'herbalist',
    3,
    [
      ['resin', 2],
      ['herb-leaf', 2],
    ],
    { good: 'calm-incense', n: 1 },
    5,
    16,
  ),
  // ── เครื่องมือขั้นเหล็กและเหล็กดำ (data/tools.ts): the blacksmith, each from the tier before ──
  ...TOOL_RECIPES,
  ...BREAKTHROUGH_RECIPES,
];
export const RECIPE_LIST: readonly RecipeDef[] = recipes;
export const RECIPES: Readonly<Record<string, RecipeDef>> = Object.fromEntries(recipes.map((x) => [x.id, x]));

/** Most of one recipe queued at once. */
export const CRAFT_BATCH_MAX = 20;
/** How close (tiles) the player must stand to a node to work it. */
export const NODE_REACH = 1.6;
